import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { X, Settings, Plus, Trash2, Edit2, MoreVertical, Eye, Calendar, Search, FileUp, Upload, Download } from "lucide-react";
import toast from "react-hot-toast";
import api, { branchService, departmentService, designationService, subDepartmentService, gradesService, categoryService } from "../../services/api";
import Filters from "./Filters";
import WorkflowManagement from "./WorkflowManagement";
import CommunicationPollsPanel from "./CommunicationPollsPanel";
import UserRoleManagement from "./user-role-management/UserRoleManagement";
import { usePermissions } from "../../rbac/PermissionsContext";
import { getAuthJson, getAuthItem } from "../../utils/authStorage";
import { ADMIN_SHELL_NAV_EVENT } from "../../utils/adminShellNav";

const COMMUNICATION_SUB_TABS = new Set([
  "flash_news",
  "announcement",
  "polls_advanced",
  "polls_manage",
]);

function toDatetimeLocalValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultDisplayWindow() {
  const from = new Date();
  const until = new Date(from.getTime() + 24 * 60 * 60 * 1000);
  return {
    displayFrom: toDatetimeLocalValue(from),
    displayUntil: toDatetimeLocalValue(until),
  };
}

export default function GlobalSettingsModal({ initialTab = "communication" }) {
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activeSubTab, setActiveSubTab] = useState("flash_news"); // Sub-tab for communication
  const [activeRoleSubTab, setActiveRoleSubTab] = useState("role_creation");
  const isCommunicationNavMode = initialTab === "communication";
  const isRoleManagementNavMode = initialTab === "user_role_management";

  const applyShellTab = (tab) => {
    if (!tab) return;
    if (COMMUNICATION_SUB_TABS.has(tab)) {
      setActiveTab("communication");
      setActiveSubTab(tab);
      return;
    }
    if (tab === "policy") {
      setActiveTab("policy");
      return;
    }
    if (tab === "user_role_management") {
      setActiveTab("user_role_management");
      setActiveRoleSubTab("role_creation");
    }
  };

  useEffect(() => {
    setActiveTab(initialTab || "communication");
    if (initialTab === "communication") setActiveSubTab("flash_news");
  }, [initialTab]);

  useEffect(() => {
    applyShellTab(searchParams.get("tab"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const onShellNav = (event) => {
      const moduleKey = event?.detail?.module;
      if (
        moduleKey !== "communication" &&
        moduleKey !== "user_roles" &&
        moduleKey !== "configuration"
      ) {
        return;
      }
      // Only react when this page owns the module
      if (isCommunicationNavMode && moduleKey === "user_roles") return;
      if (isRoleManagementNavMode && moduleKey === "communication") return;
      applyShellTab(event?.detail?.tab);
    };
    window.addEventListener(ADMIN_SHELL_NAV_EVENT, onShellNav);
    return () => window.removeEventListener(ADMIN_SHELL_NAV_EVENT, onShellNav);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCommunicationNavMode, isRoleManagementNavMode]);
  const [flashNews, setFlashNews] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [showFlashNewsForm, setShowFlashNewsForm] = useState(false);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [showPolicyForm, setShowPolicyForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterResults, setFilterResults] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const [calendarForMonth, setCalendarForMonth] = useState(null); // For monthly, this will be "Monthly", for others it will be the selected month
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const monthDropdownRef = useRef(null);
  const [showAcknowledgementSettings, setShowAcknowledgementSettings] = useState(false);
  const [requireAcknowledgement, setRequireAcknowledgement] = useState(false);
  const [searchFlashNews, setSearchFlashNews] = useState("");
  const [searchAnnouncements, setSearchAnnouncements] = useState("");
  const canViewCommunication =
    isAdmin ||
    hasModuleAccess("communication", "view") ||
    hasScreenAccess("communication", "flash_news", "view") ||
    hasScreenAccess("communication", "announcements", "view") ||
    hasScreenAccess("communication", "polls", "view");
  const canViewFlashNews =
    isAdmin ||
    hasModuleAccess("communication", "view") ||
    hasScreenAccess("communication", "flash_news", "view");
  const canViewAnnouncements =
    isAdmin ||
    hasModuleAccess("communication", "view") ||
    hasScreenAccess("communication", "announcements", "view");
  const canViewPolicy =
    isAdmin || hasScreenAccess("communication", "company_policy", "view");
  const canCreatePolicy =
    isAdmin || hasScreenAccess("communication", "company_policy", "create");
  const canEditPolicy =
    isAdmin || hasScreenAccess("communication", "company_policy", "edit");
  const canDeletePolicy =
    isAdmin || hasScreenAccess("communication", "company_policy", "delete");
  const canCreateFlashNews =
    isAdmin ||
    hasModuleAccess("communication", "create") ||
    hasScreenAccess("communication", "flash_news", "create");
  const canEditFlashNews =
    isAdmin ||
    hasModuleAccess("communication", "edit") ||
    hasScreenAccess("communication", "flash_news", "edit");
  const canDeleteFlashNews =
    isAdmin ||
    hasModuleAccess("communication", "delete") ||
    hasScreenAccess("communication", "flash_news", "delete");
  const canCreateAnnouncements =
    isAdmin ||
    hasModuleAccess("communication", "create") ||
    hasScreenAccess("communication", "announcements", "create");
  const canEditAnnouncements =
    isAdmin ||
    hasModuleAccess("communication", "edit") ||
    hasScreenAccess("communication", "announcements", "edit");
  const canDeleteAnnouncements =
    isAdmin ||
    hasModuleAccess("communication", "delete") ||
    hasScreenAccess("communication", "announcements", "delete");
  const canViewPolls =
    isAdmin || hasScreenAccess("communication", "polls", "view");
  const canCreatePolls =
    isAdmin ||
    hasModuleAccess("communication", "create") ||
    hasScreenAccess("communication", "polls", "create");
  const canEditPolls =
    isAdmin ||
    hasModuleAccess("communication", "edit") ||
    hasScreenAccess("communication", "polls", "edit");
  const canDeletePolls =
    isAdmin ||
    hasModuleAccess("communication", "delete") ||
    hasScreenAccess("communication", "polls", "delete");
  useEffect(() => {
    if (activeTab !== "communication") return;
    const allowed = {
      flash_news: canViewFlashNews,
      announcement: canViewAnnouncements,
      polls_manage: canViewPolls,
      polls_advanced: canViewPolls,
    };
    if (allowed[activeSubTab] !== false) return;
    const firstAllowed = Object.keys(allowed).find((k) => allowed[k]);
    if (!firstAllowed && canViewPolicy) {
      setActiveTab("policy");
      return;
    }
    setActiveSubTab(firstAllowed || "flash_news");
  }, [activeTab, activeSubTab, canViewFlashNews, canViewAnnouncements, canViewPolls, canViewPolicy]);
  const canViewUserRoles =
    isAdmin ||
    hasModuleAccess("user_roles", "view") ||
    hasScreenAccess("user_roles", "role_list", "view") ||
    hasScreenAccess("user_roles", "screen_access", "view") ||
    hasScreenAccess("user_roles", "data_access", "view");
  
  // User Role Management (shared slices kept here; the rest is encapsulated
  // inside <UserRoleManagement />). Roles are exposed so WorkflowManagement
  // can reuse the same list without an extra fetch.
  const [roles, setRoles] = useState([]);
  const [rolesMeta, setRolesMeta] = useState({});
  // Keep the raw user system role for legacy dependencies. RBAC-gated role
  // management visibility is controlled by `canViewUserRoles` above.
  const [currentUserRole, setCurrentUserRole] = useState(() => {
    try {
      const u = getAuthJson("currentUser", null);
      const r = String(u?.role || getAuthItem("userType") || "").toLowerCase();
      if (r === "super_admin" || r === "admin" || r === "employee") return r;
    } catch (_) {
      // ignore JSON parse errors, fall through to employee
    }
    return "employee";
  });

  
  // Level data
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    // Flash News / Announcement fields
    title: "",
    content: "",
    type: "flash_news", // 'flash_news' or 'announcement'
    recurringType: "none", // 'none', 'daily', 'weekly', 'monthly', 'custom_months', 'custom'
    ...defaultDisplayWindow(),
    startDate: "",
    endDate: "",
    weekday: "", // For weekly: Monday, Tuesday, etc.
    months: [], // For custom_months - array of selected months
    selectedDates: [], // Array of selected dates for month-based recurring types {month: "January", day: 3}
    // Assignment type: null (none selected), "all", or "filter"
    assignmentType: "all",
    // Filter results when using filter
    filterData: null,
    // Require acknowledgement (for announcements)
    requireAcknowledgement: false,
    // Policy fields
    policyTitle: "",
    policyCode: "",
    policyCategory: "",
    policyType: "mandatory", // mandatory or optional
    description: "",
    document: null,
    language: "en",
    allowDownload: false,
    allowPrint: false,
    // Effective & Tracking Dates
    effectiveFrom: "",
    effectiveTo: "",
    acknowledgementStartDate: "",
    acknowledgementDueDate: "",
    gracePeriod: "",
    reviewDate: "",
    policyExpiryDate: "",
    // Assignment (for policies: "all", "department", "location", "grade", "individual")
    assignedDepartments: [],
    assignedLocations: [],
    assignedGrades: [],
    assignedEmployees: [],
    autoAssignNewJoiners: false,
    excludedEmployees: [],
    // Acknowledgement Rules
    acknowledgementRequired: false,
    acknowledgementMethod: "checkbox", // "checkbox" or "signature"
    forceReadRule: false,
    reAcknowledgementOnUpdate: false,
    // Version Control
    version: "1.0",
    // Quiz/Assessment
    hasQuiz: false,
    quizQuestions: [],
    // Workflow fields
    workflowName: "",
  });

  useEffect(() => {
    fetchFlashNews();
    fetchAnnouncements();
    fetchPolicies();
    fetchLevels();
    if (canViewUserRoles) {
      fetchRoles();
    }
  }, [currentUserRole, canViewUserRoles]);

  // Close month dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target)) {
        setShowMonthDropdown(false);
      }
    };

    if (showMonthDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMonthDropdown]);

  const fetchRoles = async () => {
    try {
      const response = await api.get("/global-settings/roles");
      if (response.data.success) {
        // Filter out Admin and Super Admin; keep all custom Action/Access roles.
        const filtered = (response.data.data || []).filter(
          (r) =>
            String(r.code || "").toUpperCase() !== "ADMIN" &&
            String(r.code || "").toUpperCase() !== "SUPER_ADMIN"
        );
        setRoles(filtered);
        setRolesMeta(response.data.meta || {});
        return filtered;
      }
      return [];
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error(error?.response?.data?.message || "Could not load roles. Please refresh and try again.");
      return [];
    }
  };

  const fetchLevels = async () => {
    try {
      const [branchesRes, deptsRes, designationsRes, subDeptsRes, gradesRes, categoriesRes] = await Promise.all([
        branchService.getBranches(false),
        departmentService.getDepartments(false),
        designationService.getDesignations(false),
        subDepartmentService.getAllSubDepartments(false),
        gradesService.getGrades(false),
        categoryService.getAllCategories(false),
      ]);

      if (branchesRes.success) setBranches(branchesRes.data || []);
      if (deptsRes.success) setDepartments(deptsRes.data || []);
      if (designationsRes.success) setDesignations(designationsRes.data || []);
      if (subDeptsRes.success) setSubDepartments(subDeptsRes.data || []);
      if (gradesRes.success) setGrades(gradesRes.data || []);
      if (categoriesRes.success) setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error("Error fetching levels:", error);
    }
  };

  const fetchFlashNews = async () => {
    try {
      // Try API first, fallback to localStorage
      const response = await api.get("/global-settings/flash-news");
      if (response.data.success) {
        setFlashNews(response.data.data || []);
      }
    } catch (error) {
      // Fallback to localStorage for testing
      const stored = localStorage.getItem("flashNews");
      if (stored) {
        setFlashNews(JSON.parse(stored));
      } else {
        setFlashNews([]);
      }
    }
  };

  const fetchAnnouncements = async () => {
    try {
      // Try API first, fallback to localStorage
      const response = await api.get("/global-settings/announcements");
      if (response.data.success) {
        setAnnouncements(response.data.data || []);
      }
    } catch (error) {
      // Fallback to localStorage for testing
      const stored = localStorage.getItem("announcements");
      if (stored) {
        setAnnouncements(JSON.parse(stored));
      } else {
        setAnnouncements([]);
      }
    }
  };

  const fetchPolicies = async () => {
    try {
      const response = await api.get("/global-settings/policies");
      if (response.data.success) {
        setPolicies(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching policies:", error);
      // Fallback to localStorage for testing
      const stored = localStorage.getItem("policies");
      if (stored) {
        setPolicies(JSON.parse(stored));
      } else {
        setPolicies([]);
      }
    }
  };


  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files[0]) {
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleMonthToggle = (month) => {
    const currentMonths = formData.months || [];
    const newMonths = currentMonths.includes(month)
      ? currentMonths.filter(m => m !== month)
      : [...currentMonths, month];
    setFormData({ ...formData, months: newMonths });
  };

  const handleDateToggle = (month, day) => {
    const currentDates = formData.selectedDates || [];
    const dateExists = currentDates.some(d => d.month === month && d.day === day);
    const newDates = dateExists
      ? currentDates.filter(d => !(d.month === month && d.day === day))
      : [...currentDates, { month, day }];
    setFormData({ ...formData, selectedDates: newDates });
  };

  const handleMonthSelect = (e) => {
    const selectedValues = Array.from(e.target.selectedOptions, option => option.value);
    setFormData({ ...formData, months: selectedValues });
  };

  const openCalendarPopup = (month) => {
    setCalendarForMonth(month);
    setShowCalendarPopup(true);
  };

  const handleCalendarDateToggle = (day) => {
    const currentDates = formData.selectedDates || [];
    const dateExists = currentDates.some(d => d.month === calendarForMonth && d.day === day);
    const newDates = dateExists
      ? currentDates.filter(d => !(d.month === calendarForMonth && d.day === day))
      : [...currentDates, { month: calendarForMonth, day }];
    setFormData({ ...formData, selectedDates: newDates });
  };

  const handleClearCalendarDates = () => {
    const currentDates = formData.selectedDates || [];
    const newDates = currentDates.filter(d => d.month !== calendarForMonth);
    setFormData({ ...formData, selectedDates: newDates });
  };

  const handleMonthCheckboxToggle = (month) => {
    const selectedMonths = formData.months || [];
    const isSelected = selectedMonths.includes(month);
    const newMonths = isSelected
      ? selectedMonths.filter(m => m !== month)
      : [...selectedMonths, month];
    setFormData({ ...formData, months: newMonths });
  };

  const handleClearMonthSelection = () => {
    setFormData({ ...formData, months: [], selectedDates: [] });
    setShowMonthDropdown(false);
  };


  const handleAssignmentTypeChange = (type) => {
    setFormData({ ...formData, assignmentType: type, filterData: type === "filter" ? filterResults : null });
    if (type === "filter") {
      setShowFilters(true);
    } else {
      setShowFilters(false);
    }
  };

  const handleFilterApply = (employeesData, filters, params) => {
    setFilterResults({ employeesData, filters, params });
    setFormData({ ...formData, filterData: { employeesData, filters, params } });
    setShowFilters(false);
  };

  const handleFilterClose = () => {
    setShowFilters(false);
  };

  const handleFlashNewsSubmit = async (e) => {
    e.preventDefault();
    if (!canCreateFlashNews && !(editingItem && canEditFlashNews)) {
      toast.error("You do not have permission to save flash news.");
      return;
    }

    if (!formData.assignmentType) {
      toast.error("Please choose Assign All or Assign using filter.");
      return;
    }

    // Validate filter selection
    if (formData.assignmentType === "filter" && !formData.filterData) {
      toast.error("Please apply a filter before saving.");
      setShowFilters(true);
      return;
    }

    if (!formData.displayFrom || !formData.displayUntil) {
      toast.error("Please set Display From and Display Until.");
      return;
    }
    const fromMs = new Date(formData.displayFrom).getTime();
    const untilMs = new Date(formData.displayUntil).getTime();
    if (!Number.isFinite(fromMs) || !Number.isFinite(untilMs)) {
      toast.error("Invalid display window dates.");
      return;
    }
    if (untilMs <= fromMs) {
      toast.error("Display Until must be after Display From.");
      return;
    }

    // Validate recurring type specific fields
    if (formData.recurringType === "weekly" && !formData.weekday) {
      toast.error("Please select a weekday.");
      return;
    }
    if (formData.recurringType === "custom_months") {
      if (formData.months.length === 0) {
        toast.error("Please select at least one month.");
        return;
      }
      if (formData.selectedDates.length === 0) {
        toast.error("Please select at least one date.");
        return;
      }
    }
    if (formData.recurringType === "monthly" && formData.selectedDates.length === 0) {
      toast.error("Please select at least one day.");
      return;
    }
    if (formData.recurringType === "custom" && (!formData.startDate || !formData.endDate)) {
      toast.error("Please set start and end dates for the custom period.");
      return;
    }

    try {
      const payload = {
        title: formData.title,
        content: formData.content,
        type: "flash_news",
        recurringType: formData.recurringType,
        displayFrom: new Date(formData.displayFrom).toISOString(),
        displayUntil: new Date(formData.displayUntil).toISOString(),
        startDate: formData.recurringType === "custom" ? formData.startDate : null,
        endDate: formData.recurringType === "custom" ? formData.endDate : null,
        weekday: formData.recurringType === "weekly" ? formData.weekday : null,
        months: formData.recurringType === "custom_months" ? formData.months : [],
        selectedDates: ["custom_months", "monthly"].includes(formData.recurringType)
          ? formData.selectedDates
          : [],
        assignmentType: formData.assignmentType,
        filterData: formData.filterData,
        status: editingItem ? editingItem.status || "active" : "active",
      };

      // Try API first, fallback to localStorage
      try {
        if (editingItem) {
          await api.put(`/global-settings/flash-news/${editingItem.id}`, payload);
        } else {
          await api.post("/global-settings/flash-news", payload);
        }
      } catch (apiError) {
        // Save to localStorage for testing
        const stored = localStorage.getItem("flashNews");
        const currentList = stored ? JSON.parse(stored) : [];
        const localPayload = {
          ...payload,
          id: editingItem ? editingItem.id : Date.now(),
          createdAt: editingItem ? editingItem.createdAt : new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        if (editingItem) {
          const updatedList = currentList.map((item) =>
            item.id === editingItem.id ? localPayload : item
          );
          localStorage.setItem("flashNews", JSON.stringify(updatedList));
        } else {
          currentList.push(localPayload);
          localStorage.setItem("flashNews", JSON.stringify(currentList));
        }
      }

      try {
        window.dispatchEvent(new Event("pace-flash-news-updated"));
      } catch (_) {
        /* ignore */
      }

      await fetchFlashNews();
      resetForm();
      setShowFlashNewsForm(false);
      toast.success(editingItem ? "Flash news updated." : "Flash news saved.");
    } catch (error) {
      console.error("Error saving flash news:", error);
      toast.error("Failed to save flash news. Please try again.");
    }
  };

  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();
    if (!canCreateAnnouncements && !(editingItem && canEditAnnouncements)) {
      toast.error("You do not have permission to save announcements.");
      return;
    }
    
    // Validate filter selection
    if (formData.assignmentType === "filter" && !formData.filterData) {
      toast.error("Please apply a filter before saving.");
      setShowFilters(true);
      return;
    }

    // Validate recurring type specific fields
    if (formData.recurringType === "weekly" && !formData.weekday) {
      toast.error("Please select a weekday.");
      return;
    }
    if (formData.recurringType === "custom_months") {
      if (formData.months.length === 0) {
        toast.error("Please select at least one month.");
        return;
      }
      if (formData.selectedDates.length === 0) {
        toast.error("Please select at least one date.");
        return;
      }
    }
    if (formData.recurringType === "monthly" && formData.selectedDates.length === 0) {
      toast.error("Please select at least one day.");
      return;
    }

    try {
      const payload = {
        id: editingItem ? editingItem.id : Date.now(),
        title: formData.title,
        content: formData.content,
        type: "announcement",
        recurringType: formData.recurringType,
        startDate: formData.recurringType === "none" ? null : (formData.recurringType === "custom" ? formData.startDate : null),
        endDate: formData.recurringType === "custom" ? formData.endDate : null,
        weekday: formData.recurringType === "weekly" ? formData.weekday : null,
        months: formData.recurringType === "custom_months" ? formData.months : [],
        selectedDates: ["custom_months", "monthly"].includes(formData.recurringType) ? formData.selectedDates : [],
        assignmentType: formData.assignmentType,
        filterData: formData.filterData,
        requireAcknowledgement: formData.requireAcknowledgement,
        status: editingItem ? editingItem.status || "active" : "active",
        createdAt: editingItem ? editingItem.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Try API first, fallback to localStorage
      try {
        if (editingItem) {
          await api.put(`/global-settings/announcements/${editingItem.id}`, payload);
        } else {
          await api.post("/global-settings/announcements", payload);
        }
      } catch (apiError) {
        // Save to localStorage for testing
        const stored = localStorage.getItem("announcements");
        const currentList = stored ? JSON.parse(stored) : [];
        
        if (editingItem) {
          const updatedList = currentList.map(item => 
            item.id === editingItem.id ? payload : item
          );
          localStorage.setItem("announcements", JSON.stringify(updatedList));
        } else {
          currentList.push(payload);
          localStorage.setItem("announcements", JSON.stringify(currentList));
        }
      }

      await fetchAnnouncements();
      resetForm();
      setShowAnnouncementForm(false);
    } catch (error) {
      console.error("Error saving announcement:", error);
      toast.error("Failed to save announcement. Please try again.");
    }
  };

  const handlePolicySubmit = async (e) => {
    e.preventDefault();
    if (editingItem ? !canEditPolicy : !canCreatePolicy) {
      toast.error("You do not have permission to save company policies.");
      return;
    }
    try {
      const formDataToSend = new FormData();
      // Basic Information
      formDataToSend.append("policyTitle", formData.policyTitle);
      formDataToSend.append("policyCode", formData.policyCode);
      formDataToSend.append("policyCategory", formData.policyCategory);
      formDataToSend.append("policyType", formData.policyType);
      formDataToSend.append("description", formData.description || "");
      formDataToSend.append("language", formData.language);
      formDataToSend.append("allowDownload", formData.allowDownload);
      formDataToSend.append("allowPrint", formData.allowPrint);
      if (formData.document) {
        formDataToSend.append("document", formData.document);
      }
      // Effective & Tracking Dates
      formDataToSend.append("effectiveFrom", formData.effectiveFrom);
      formDataToSend.append("effectiveTo", formData.effectiveTo || "");
      formDataToSend.append("acknowledgementStartDate", formData.acknowledgementStartDate);
      formDataToSend.append("acknowledgementDueDate", formData.acknowledgementDueDate);
      formDataToSend.append("gracePeriod", formData.gracePeriod || "");
      formDataToSend.append("reviewDate", formData.reviewDate || "");
      formDataToSend.append("policyExpiryDate", formData.policyExpiryDate || "");
      formDataToSend.append("version", formData.version || "1.0");
      // Assignment
      formDataToSend.append("assignmentType", formData.assignmentType || "");
      formDataToSend.append("assignedDepartments", JSON.stringify(formData.assignedDepartments || []));
      formDataToSend.append("assignedLocations", JSON.stringify(formData.assignedLocations || []));
      formDataToSend.append("assignedGrades", JSON.stringify(formData.assignedGrades || []));
      formDataToSend.append("assignedEmployees", JSON.stringify(formData.assignedEmployees || []));
      formDataToSend.append("autoAssignNewJoiners", formData.autoAssignNewJoiners);
      formDataToSend.append("excludedEmployees", JSON.stringify(formData.excludedEmployees || []));
      // Acknowledgement Rules
      formDataToSend.append("acknowledgementRequired", formData.acknowledgementRequired);
      formDataToSend.append("acknowledgementMethod", formData.acknowledgementMethod || "checkbox");
      formDataToSend.append("forceReadRule", formData.forceReadRule);
      formDataToSend.append("reAcknowledgementOnUpdate", formData.reAcknowledgementOnUpdate);
      // Quiz
      formDataToSend.append("hasQuiz", formData.hasQuiz || false);
      formDataToSend.append("quizQuestions", JSON.stringify(formData.quizQuestions || []));

      try {
        if (editingItem) {
          await api.put(`/global-settings/policies/${editingItem.id}`, formDataToSend, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          await api.post("/global-settings/policies", formDataToSend, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      } catch (apiError) {
        // Save to localStorage for testing
        const payload = {
          id: editingItem ? editingItem.id : Date.now().toString(),
          policyTitle: formData.policyTitle,
          policyCode: formData.policyCode,
          policyCategory: formData.policyCategory,
          policyType: formData.policyType,
          description: formData.description,
          language: formData.language,
          allowDownload: formData.allowDownload,
          allowPrint: formData.allowPrint,
          effectiveFrom: formData.effectiveFrom,
          effectiveTo: formData.effectiveTo,
          acknowledgementStartDate: formData.acknowledgementStartDate,
          acknowledgementDueDate: formData.acknowledgementDueDate,
          gracePeriod: formData.gracePeriod,
          reviewDate: formData.reviewDate,
          policyExpiryDate: formData.policyExpiryDate,
          version: formData.version,
          assignmentType: formData.assignmentType,
          assignedDepartments: formData.assignedDepartments,
          assignedLocations: formData.assignedLocations,
          assignedGrades: formData.assignedGrades,
          assignedEmployees: formData.assignedEmployees,
          autoAssignNewJoiners: formData.autoAssignNewJoiners,
          excludedEmployees: formData.excludedEmployees,
          acknowledgementRequired: formData.acknowledgementRequired,
          acknowledgementMethod: formData.acknowledgementMethod,
          forceReadRule: formData.forceReadRule,
          reAcknowledgementOnUpdate: formData.reAcknowledgementOnUpdate,
          hasQuiz: formData.hasQuiz,
          quizQuestions: formData.quizQuestions,
          createdAt: editingItem ? editingItem.createdAt : new Date().toISOString(),
        };
        
        const stored = localStorage.getItem("policies");
        const currentList = stored ? JSON.parse(stored) : [];
        
        if (editingItem) {
          const updatedList = currentList.map(item => 
            item.id === editingItem.id ? payload : item
          );
          localStorage.setItem("policies", JSON.stringify(updatedList));
        } else {
          currentList.push(payload);
          localStorage.setItem("policies", JSON.stringify(currentList));
        }
      }

      await fetchPolicies();
      resetForm();
      setShowPolicyForm(false);
    } catch (error) {
      console.error("Error saving policy:", error);
      alert("Failed to save policy. Please try again.");
    }
  };

  const handleDelete = async (type, id) => {
    if (type === "flash_news" && !canDeleteFlashNews) {
      toast.error("You do not have permission to delete flash news.");
      return;
    }
    if (type === "announcement" && !canDeleteAnnouncements) {
      toast.error("You do not have permission to delete announcements.");
      return;
    }
    if (type === "policy" && !canDeletePolicy) {
      toast.error("You do not have permission to delete company policies.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      if (type === "flash_news") {
        try {
          await api.delete(`/global-settings/flash-news/${id}`);
        } catch (apiError) {
          // Delete from localStorage for testing
          const stored = localStorage.getItem("flashNews");
          if (stored) {
            const currentList = JSON.parse(stored);
            const updatedList = currentList.filter(item => item.id !== id);
            localStorage.setItem("flashNews", JSON.stringify(updatedList));
          }
        }
        await fetchFlashNews();
      } else if (type === "announcement") {
        try {
          await api.delete(`/global-settings/announcements/${id}`);
        } catch (apiError) {
          // Delete from localStorage for testing
          const stored = localStorage.getItem("announcements");
          if (stored) {
            const currentList = JSON.parse(stored);
            const updatedList = currentList.filter(item => item.id !== id);
            localStorage.setItem("announcements", JSON.stringify(updatedList));
          }
        }
        await fetchAnnouncements();
      } else if (type === "policy") {
        await api.delete(`/global-settings/policies/${id}`);
        await fetchPolicies();
      }
      if (type === "flash_news") {
        try {
          window.dispatchEvent(new Event("pace-flash-news-updated"));
        } catch (_) {
          /* ignore */
        }
      }
      setOpenMenuId(null);
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item. Please try again.");
    }
  };

  const handleDeactivate = async (type, id) => {
    if (type === "flash_news" && !canEditFlashNews) {
      toast.error("You do not have permission to deactivate flash news.");
      return;
    }
    if (type === "announcement" && !canEditAnnouncements) {
      toast.error("You do not have permission to deactivate announcements.");
      return;
    }
    if (!window.confirm("Are you sure you want to deactivate this item?")) return;

    try {
      const payload = { status: "inactive" };
      if (type === "flash_news") {
        try {
          await api.put(`/global-settings/flash-news/${id}`, payload);
        } catch (apiError) {
          // Update in localStorage for testing
          const stored = localStorage.getItem("flashNews");
          if (stored) {
            const currentList = JSON.parse(stored);
            const updatedList = currentList.map(item => 
              item.id === id ? { ...item, status: "inactive", updatedAt: new Date().toISOString() } : item
            );
            localStorage.setItem("flashNews", JSON.stringify(updatedList));
          }
        }
        await fetchFlashNews();
      } else if (type === "announcement") {
        try {
          await api.put(`/global-settings/announcements/${id}`, payload);
        } catch (apiError) {
          // Update in localStorage for testing
          const stored = localStorage.getItem("announcements");
          if (stored) {
            const currentList = JSON.parse(stored);
            const updatedList = currentList.map(item => 
              item.id === id ? { ...item, status: "inactive", updatedAt: new Date().toISOString() } : item
            );
            localStorage.setItem("announcements", JSON.stringify(updatedList));
          }
        }
        await fetchAnnouncements();
      }
      if (type === "flash_news") {
        try {
          window.dispatchEvent(new Event("pace-flash-news-updated"));
        } catch (_) {
          /* ignore */
        }
      }
      setOpenMenuId(null);
    } catch (error) {
      console.error("Error deactivating item:", error);
      toast.error("Failed to deactivate item. Please try again.");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEdit = (item, type) => {
    if (type === "flash_news" && !canEditFlashNews) {
      toast.error("You do not have permission to edit flash news.");
      return;
    }
    if (type === "announcement" && !canEditAnnouncements) {
      toast.error("You do not have permission to edit announcements.");
      return;
    }
    if (type === "policy" && !canEditPolicy) {
      toast.error("You do not have permission to edit company policies.");
      return;
    }
    setEditingItem(item);
    if (type === "flash_news" || type === "announcement") {
      setFormData({
        title: item.title || "",
        content: item.content || "",
        type: type,
        recurringType: item.recurringType || "none",
        displayFrom: toDatetimeLocalValue(item.displayFrom) || defaultDisplayWindow().displayFrom,
        displayUntil: toDatetimeLocalValue(item.displayUntil) || defaultDisplayWindow().displayUntil,
        startDate: item.startDate || "",
        endDate: item.endDate || "",
        weekday: item.weekday || "",
        months: item.months || [],
        selectedDates: item.selectedDates || [],
        assignmentType: item.assignmentType || "all",
        filterData: item.filterData || null,
        requireAcknowledgement: item.requireAcknowledgement !== undefined ? item.requireAcknowledgement : false,
        policyName: "",
        revisionDate: "",
        effectiveFrom: "",
        document: null,
      });
      if (item.filterData) {
        setFilterResults(item.filterData);
      }
      if (type === "flash_news") {
        setShowFlashNewsForm(true);
        setActiveSubTab("flash_news");
      } else {
        setShowAnnouncementForm(true);
        setActiveSubTab("announcement");
      }
    } else if (type === "policy") {
      setFormData({
        title: "",
        content: "",
        type: "flash_news",
        recurringType: "none",
        startDate: "",
        endDate: "",
        weekday: "",
        months: [],
        selectedDates: [],
        assignmentType: item.assignmentType || null,
        filterData: null,
        requireAcknowledgement: false,
        // Policy fields
        policyTitle: item.policyTitle || item.name || "",
        policyCode: item.policyCode || "",
        policyCategory: item.policyCategory || "",
        policyType: item.policyType || "mandatory",
        description: item.description || "",
        document: null,
        language: item.language || "en",
        allowDownload: item.allowDownload !== undefined ? item.allowDownload : false,
        allowPrint: item.allowPrint !== undefined ? item.allowPrint : false,
        effectiveFrom: item.effectiveFrom || "",
        effectiveTo: item.effectiveTo || "",
        acknowledgementStartDate: item.acknowledgementStartDate || "",
        acknowledgementDueDate: item.acknowledgementDueDate || "",
        gracePeriod: item.gracePeriod || "",
        reviewDate: item.reviewDate || "",
        policyExpiryDate: item.policyExpiryDate || "",
        assignedDepartments: item.assignedDepartments || [],
        assignedLocations: item.assignedLocations || [],
        assignedGrades: item.assignedGrades || [],
        assignedEmployees: item.assignedEmployees || [],
        autoAssignNewJoiners: item.autoAssignNewJoiners || false,
        excludedEmployees: item.excludedEmployees || [],
        acknowledgementRequired: item.acknowledgementRequired !== undefined ? item.acknowledgementRequired : false,
        acknowledgementMethod: item.acknowledgementMethod || "checkbox",
        forceReadRule: item.forceReadRule || false,
        reAcknowledgementOnUpdate: item.reAcknowledgementOnUpdate || false,
        version: item.version || "1.0",
        hasQuiz: item.hasQuiz || false,
        quizQuestions: item.quizQuestions || [],
      });
      setShowPolicyForm(true);
    }
  };

  const resetForm = () => {
    const windowDefaults = defaultDisplayWindow();
    setFormData({
      title: "",
      content: "",
      type: "flash_news",
      recurringType: "none",
      displayFrom: windowDefaults.displayFrom,
      displayUntil: windowDefaults.displayUntil,
      startDate: "",
      endDate: "",
      weekday: "",
      months: [],
      selectedDates: [],
      assignmentType: "all",
      filterData: null,
      requireAcknowledgement: false,
      // Policy fields
      policyTitle: "",
      policyCode: "",
      policyCategory: "",
      policyType: "mandatory",
      description: "",
      document: null,
      language: "en",
      allowDownload: false,
      allowPrint: false,
      effectiveFrom: "",
      effectiveTo: "",
      acknowledgementStartDate: "",
      acknowledgementDueDate: "",
      gracePeriod: "",
      reviewDate: "",
      policyExpiryDate: "",
      assignedDepartments: [],
      assignedLocations: [],
      assignedGrades: [],
      assignedEmployees: [],
      autoAssignNewJoiners: false,
      excludedEmployees: [],
      acknowledgementRequired: false,
      acknowledgementMethod: "checkbox",
      forceReadRule: false,
      reAcknowledgementOnUpdate: false,
      version: "1.0",
      hasQuiz: false,
      quizQuestions: [],
      // User Role Management fields
      roleName: "",
      roleDescription: "",
      inheritFromRole: "",
    });
    setEditingItem(null);
    setFilterResults(null);
    setShowFilters(false);
  };

  const handleCancel = () => {
    resetForm();
    setShowFlashNewsForm(false);
    setShowAnnouncementForm(false);
    setShowPolicyForm(false);
  };


  const renderFormPopup = (type) => {
    const isFlashNews = type === "flash_news";
    const isAnnouncement = type === "announcement";
    const isOpen = isFlashNews ? showFlashNewsForm : showAnnouncementForm;
    const onSubmit = isFlashNews ? handleFlashNewsSubmit : handleAnnouncementSubmit;
    const title = isFlashNews ? (editingItem ? "Edit Flash News" : "Add Flash News") : (editingItem ? "Edit Announcement" : "Add Announcement");

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[hsl(var(--ds-fg))/0.45] p-4">
        <div
          className={`relative flex w-full max-w-[920px] flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)] ${
            isFlashNews ? "max-h-[min(88vh,760px)]" : "h-[min(88vh,700px)] max-h-[700px]"
          }`}
        >
          {/* Fixed Header */}
          <div className="flex flex-shrink-0 items-center justify-between border-b border-[hsl(var(--ds-border))] px-5 py-4">
            <div>
              <h3 className="ds-h3 text-[hsl(var(--ds-fg))]">{title}</h3>
              {isFlashNews ? (
                <p className="mt-0.5 ds-caption text-[hsl(var(--ds-muted-fg))]">
                  Target audience sees a rolling highlight in the top nav until Display Until.
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-full p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-5">
            <form onSubmit={onSubmit} id={`${type}-form`}>
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="mb-1 block ds-caption text-[hsl(var(--ds-fg))]">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
                    required
                  />
                </div>
                
                {/* Content */}
                <div>
                  <label className="mb-1 block ds-caption text-[hsl(var(--ds-fg))]">Content</label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
                    rows={isFlashNews ? 3 : 2}
                    required
                  />
                </div>

                {isFlashNews && (
                  <div className="grid grid-cols-1 gap-3 rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 p-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block ds-caption text-[hsl(var(--ds-fg))]">
                        Display From
                      </label>
                      <input
                        type="datetime-local"
                        name="displayFrom"
                        value={formData.displayFrom || ""}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block ds-caption text-[hsl(var(--ds-fg))]">
                        Display Until
                      </label>
                      <input
                        type="datetime-local"
                        name="displayUntil"
                        value={formData.displayUntil || ""}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm"
                        required
                      />
                    </div>
                    <p className="ds-small text-[hsl(var(--ds-muted-fg))] sm:col-span-2">
                      Rolling message appears in the navbar for the assigned audience only within this window.
                    </p>
                  </div>
                )}

                {/* Recurring Type and related fields */}
                <div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <div>
                      <label className="mb-1 block ds-caption text-[hsl(var(--ds-fg))]">Recurring Type</label>
                      <select
                        name="recurringType"
                        value={formData.recurringType}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm"
                      >
                        <option value="none">None (continuous in window)</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="custom_months">Custom Months</option>
                        <option value="custom">Custom Period</option>
                      </select>
                    </div>
                    {formData.recurringType === "weekly" && (
                      <div>
                        <label className="block text-[9px] font-medium mb-0.5">Select the weekday</label>
                        <select
                          name="weekday"
                          value={formData.weekday}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                          required
                        >
                          <option value="">Select Weekday</option>
                          <option value="Monday">Monday</option>
                          <option value="Tuesday">Tuesday</option>
                          <option value="Wednesday">Wednesday</option>
                          <option value="Thursday">Thursday</option>
                          <option value="Friday">Friday</option>
                          <option value="Saturday">Saturday</option>
                          <option value="Sunday">Sunday</option>
                        </select>
                      </div>
                    )}
                    {formData.recurringType === "monthly" && (
                      <div>
                        <label className="block text-[9px] font-medium mb-0.5">Select the days</label>
                        <button
                          type="button"
                          onClick={() => openCalendarPopup("Monthly")}
                          className="w-full h-[38px] min-w-[38px] p-2 border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))] flex items-center justify-center"
                          title="Open Calendar"
                        >
                          <Calendar className="w-5 h-5 text-[hsl(var(--ds-fg))]" />
                        </button>
                      </div>
                    )}
                    {formData.recurringType === "custom_months" && (
                      <div className="col-span-2">
                        <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                          <div className="relative" ref={monthDropdownRef}>
                            <label className="block text-[9px] font-medium mb-0.5">Select Months</label>
                            <button
                              type="button"
                              onClick={() => setShowMonthDropdown(!showMonthDropdown)}
                              className="w-full h-[38px] px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm text-left bg-[hsl(var(--ds-surface))] flex justify-between items-center"
                            >
                              <span className="truncate">
                                {(formData.months || []).length === 0
                                  ? "Select months..."
                                  : (formData.months || []).length === 1
                                  ? (formData.months || [])[0]
                                  : `${(formData.months || []).length} months selected`}
                              </span>
                              <span className="ml-2">â–¼</span>
                            </button>
                            {showMonthDropdown && (
                              <div className="absolute z-[100] w-full mt-1 bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-lg max-h-60 overflow-y-auto" style={{ maxWidth: '100%' }}>
                                {(formData.months || []).length > 0 && (
                                  <div className="border-b border-[hsl(var(--ds-border))]">
                                    <button
                                      type="button"
                                      onClick={handleClearMonthSelection}
                                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                    >
                                      Clear Selection
                                    </button>
                                  </div>
                                )}
                                {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map(month => {
                                  const isChecked = (formData.months || []).includes(month);
                                  return (
                                    <label
                                      key={month}
                                      className="flex items-center px-3 py-2 hover:bg-[hsl(var(--ds-muted))] cursor-pointer"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleMonthCheckboxToggle(month)}
                                        className="mr-2"
                                      />
                                      <span className="text-sm">{month}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-[9px] font-medium mb-0.5">Select the days</label>
                            <button
                              type="button"
                              onClick={() => {
                                if ((formData.months || []).length === 0) {
                                  alert("Please select at least one month first");
                                  return;
                                }
                                openCalendarPopup((formData.months || [])[0]);
                              }}
                              disabled={(formData.months || []).length === 0}
                              className="w-full h-[38px] min-w-[38px] p-2 border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))] disabled:bg-[hsl(var(--ds-muted))] disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center"
                              title="Open Calendar"
                            >
                              <Calendar className="w-5 h-5 text-[hsl(var(--ds-fg))]" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {formData.recurringType === "custom" && (
                      <>
                        <div>
                          <label className="block text-[9px] font-medium mb-0.5">Start Date</label>
                          <input
                            type="date"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleInputChange}
                            className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-medium mb-0.5">End Date</label>
                          <input
                            type="date"
                            name="endDate"
                            value={formData.endDate}
                            onChange={handleInputChange}
                            className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                            required
                          />
                        </div>
                      </>
                    )}
                    {(formData.recurringType === "none" || formData.recurringType === "daily") && (
                      <>
                        <div className="hidden sm:block" />
                        <div className="hidden sm:block" />
                      </>
                    )}
                  </div>
                  {/* Selected dates display for monthly and custom_months */}
                  {formData.recurringType === "monthly" && formData.selectedDates && formData.selectedDates.length > 0 && (
                    <div className="mt-2 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-2 text-[9px] text-[hsl(var(--ds-muted-fg))]">
                      <strong>Selected Days:</strong> {formData.selectedDates.map(d => d.day).join(", ")}
                    </div>
                  )}
                  {formData.recurringType === "custom_months" && formData.selectedDates && formData.selectedDates.length > 0 && (
                    <div className="mt-2 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-2 text-[9px] text-[hsl(var(--ds-muted-fg))]">
                      <strong>Selected:</strong> {formData.selectedDates.map(d => `${d.month} ${d.day}`).join(", ")}
                    </div>
                  )}
                </div>
                
                {/* Level Selection */}
                <div>
                  {renderLevelSelection()}
                </div>

                {/* Require Acknowledgement - Only for Announcements */}
                {!isFlashNews && (
                  <div className="border-t border-[hsl(var(--ds-border))] pt-3 mt-3">
                    <h4 className="text-sm font-semibold mb-3">Require acknowledgement</h4>
                    <div className="flex gap-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="requireAcknowledgement"
                          value="yes"
                          checked={formData.requireAcknowledgement === true}
                          onChange={() => setFormData({ ...formData, requireAcknowledgement: true })}
                          className="w-4 h-4 mr-2"
                        />
                        <span className="text-sm">Yes</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="requireAcknowledgement"
                          value="no"
                          checked={formData.requireAcknowledgement === false}
                          onChange={() => setFormData({ ...formData, requireAcknowledgement: false })}
                          className="w-4 h-4 mr-2"
                        />
                        <span className="text-sm">No</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Fixed Action Buttons */}
          <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-4">
            {isFlashNews && (
              <button
                type="button"
                onClick={() => {
                  if (!formData.title || !formData.content) {
                    toast.error("Please fill in title and content to preview");
                    return;
                  }
                  setPreviewItem({
                    title: formData.title,
                    content: formData.content,
                    recurringType: formData.recurringType,
                    weekday: formData.weekday,
                    selectedDates: formData.selectedDates,
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    displayFrom: formData.displayFrom,
                    displayUntil: formData.displayUntil,
                    assignmentType: formData.assignmentType,
                    filterData: formData.filterData,
                  });
                  setShowPreview(true);
                }}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--ds-primary))] px-4 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
              >
                <Eye className="h-4 w-4" />
                Preview
              </button>
            )}
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center justify-center rounded-full border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-4 py-2 text-sm font-semibold text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
            >
              Cancel
            </button>
            <button
              type="submit"
              form={`${type}-form`}
              className="inline-flex items-center justify-center rounded-full bg-[hsl(var(--ds-primary))] px-4 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] disabled:opacity-45"
            >
              {editingItem ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPolicyFormPopup = () => {
    if (!showPolicyForm) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[60]">
        <div className="bg-[hsl(var(--ds-surface))] shadow-xl relative w-[1200px] h-[800px] flex flex-col border border-[hsl(var(--ds-border))]">
          {/* Fixed Header */}
          <div className="flex items-center justify-between p-4 border-b border-[hsl(var(--ds-border))] flex-shrink-0">
            <h3 className="text-xl font-bold">{editingItem ? "Edit Policy" : "Add Policy"}</h3>
            <button
              onClick={handleCancel}
              className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
            >
              <X size={24} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
            <form onSubmit={handlePolicySubmit} id="policy-form">
              <div className="space-y-4">
                {/* Section 1: Basic Information */}
                <div className="border-b border-[hsl(var(--ds-border))] pb-4">
                  <h4 className="text-lg font-semibold mb-3">Basic Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Policy Title *</label>
                      <input
                        type="text"
                        name="policyTitle"
                        value={formData.policyTitle}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Policy Code *</label>
                      <input
                        type="text"
                        name="policyCode"
                        value={formData.policyCode}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Policy Category *</label>
                      <select
                        name="policyCategory"
                        value={formData.policyCategory}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                        required
                      >
                        <option value="">Select Category</option>
                        <option value="HR">HR</option>
                        <option value="IT">IT</option>
                        <option value="Finance">Finance</option>
                        <option value="Safety">Safety</option>
                        <option value="POSH">POSH</option>
                        <option value="Legal">Legal</option>
                        <option value="Operations">Operations</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Policy Type *</label>
                      <select
                        name="policyType"
                        value={formData.policyType}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                        required
                      >
                        <option value="mandatory">Mandatory</option>
                        <option value="optional">Optional</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] font-medium mb-0.5">Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                        rows="3"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Policy Document *</label>
                      <input
                        type="file"
                        name="document"
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                        accept=".pdf,.doc,.docx"
                        required={!editingItem}
                      />
                      {editingItem && formData.document === null && (
                        <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">Leave empty to keep existing document</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Language *</label>
                      <select
                        name="language"
                        value={formData.language}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                        required
                      >
                        <option value="en">English</option>
                        <option value="hi">Hindi</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Allow Download</label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="allowDownload"
                            value="yes"
                            checked={formData.allowDownload === true}
                            onChange={() => setFormData({ ...formData, allowDownload: true })}
                            className="mr-2"
                          />
                          <span className="text-sm">Yes</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="allowDownload"
                            value="no"
                            checked={formData.allowDownload === false}
                            onChange={() => setFormData({ ...formData, allowDownload: false })}
                            className="mr-2"
                          />
                          <span className="text-sm">No</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Allow Print</label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="allowPrint"
                            value="yes"
                            checked={formData.allowPrint === true}
                            onChange={() => setFormData({ ...formData, allowPrint: true })}
                            className="mr-2"
                          />
                          <span className="text-sm">Yes</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="allowPrint"
                            value="no"
                            checked={formData.allowPrint === false}
                            onChange={() => setFormData({ ...formData, allowPrint: false })}
                            className="mr-2"
                          />
                          <span className="text-sm">No</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Effective & Tracking Dates */}
                <div className="border-b border-[hsl(var(--ds-border))] pb-4">
                  <h4 className="text-lg font-semibold mb-3">Effective & Tracking Dates</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Effective From *</label>
                      <input
                        type="date"
                        name="effectiveFrom"
                        value={formData.effectiveFrom}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Effective To</label>
                      <input
                        type="date"
                        name="effectiveTo"
                        value={formData.effectiveTo}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Acknowledgement Start Date *</label>
                      <input
                        type="date"
                        name="acknowledgementStartDate"
                        value={formData.acknowledgementStartDate}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Acknowledgement Due Date *</label>
                      <input
                        type="date"
                        name="acknowledgementDueDate"
                        value={formData.acknowledgementDueDate}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Grace Period (days)</label>
                      <input
                        type="number"
                        name="gracePeriod"
                        value={formData.gracePeriod}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Review Date</label>
                      <input
                        type="date"
                        name="reviewDate"
                        value={formData.reviewDate}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Policy Expiry / Sunset Date</label>
                      <input
                        type="date"
                        name="policyExpiryDate"
                        value={formData.policyExpiryDate}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium mb-0.5">Version</label>
                      <input
                        type="text"
                        name="version"
                        value={formData.version}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 border border-[hsl(var(--ds-border))] text-sm"
                        placeholder="e.g., 1.0"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Assignment */}
                <div className="border-b border-[hsl(var(--ds-border))] pb-4">
                  <h4 className="text-lg font-semibold mb-3">Assignment</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-medium mb-2">Assign Policy To</label>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="policyAssignmentType"
                            value="all"
                            checked={formData.assignmentType === "all"}
                            onChange={(e) => setFormData({ ...formData, assignmentType: e.target.value })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">All Employees</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="policyAssignmentType"
                            value="department"
                            checked={formData.assignmentType === "department"}
                            onChange={(e) => setFormData({ ...formData, assignmentType: e.target.value })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Department</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="policyAssignmentType"
                            value="location"
                            checked={formData.assignmentType === "location"}
                            onChange={(e) => setFormData({ ...formData, assignmentType: e.target.value })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Location</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="policyAssignmentType"
                            value="grade"
                            checked={formData.assignmentType === "grade"}
                            onChange={(e) => setFormData({ ...formData, assignmentType: e.target.value })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Grade / Level</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="policyAssignmentType"
                            value="individual"
                            checked={formData.assignmentType === "individual"}
                            onChange={(e) => setFormData({ ...formData, assignmentType: e.target.value })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Individual Employees</span>
                        </label>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="autoAssignNewJoiners"
                        checked={formData.autoAssignNewJoiners}
                        onChange={(e) => setFormData({ ...formData, autoAssignNewJoiners: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <label className="text-sm">Auto assign to new joiners after DOJ</label>
                    </div>
                  </div>
                </div>

                {/* Section 4: Acknowledgement Rules */}
                <div>
                  <h4 className="text-lg font-semibold mb-3">Acknowledgement Rules</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-medium mb-2">Acknowledgement Required</label>
                      <div className="flex gap-4">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="acknowledgementRequired"
                            value="yes"
                            checked={formData.acknowledgementRequired === true}
                            onChange={() => setFormData({ ...formData, acknowledgementRequired: true })}
                            className="mr-2"
                          />
                          <span className="text-sm">Yes</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="acknowledgementRequired"
                            value="no"
                            checked={formData.acknowledgementRequired === false}
                            onChange={() => setFormData({ ...formData, acknowledgementRequired: false })}
                            className="mr-2"
                          />
                          <span className="text-sm">No</span>
                        </label>
                      </div>
                    </div>
                    {formData.acknowledgementRequired && (
                      <>
                        <div>
                          <label className="block text-[10px] font-medium mb-2">Employee Acknowledgement Method</label>
                          <div className="space-y-2">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="acknowledgementMethod"
                                value="checkbox"
                                checked={formData.acknowledgementMethod === "checkbox"}
                                onChange={(e) => setFormData({ ...formData, acknowledgementMethod: e.target.value })}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Checkbox accept</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="acknowledgementMethod"
                                value="signature"
                                checked={formData.acknowledgementMethod === "signature"}
                                onChange={(e) => setFormData({ ...formData, acknowledgementMethod: e.target.value })}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Digital signature</span>
                            </label>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            name="forceReadRule"
                            checked={formData.forceReadRule}
                            onChange={(e) => setFormData({ ...formData, forceReadRule: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <label className="text-sm">Force read rule (employee must scroll to end before acknowledging)</label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            name="reAcknowledgementOnUpdate"
                            checked={formData.reAcknowledgementOnUpdate}
                            onChange={(e) => setFormData({ ...formData, reAcknowledgementOnUpdate: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <label className="text-sm">Re-acknowledgement required on policy update/version change</label>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Fixed Action Buttons */}
          <div className="flex justify-end space-x-2 p-4 border-t border-[hsl(var(--ds-border))] flex-shrink-0">
            <button
              type="button"
              onClick={handleCancel}
              className="bg-[hsl(var(--ds-muted))] text-white px-4 py-2 hover:bg-[hsl(var(--ds-muted))]"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="policy-form"
              className="inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 rounded-md px-4 py-2 hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
            >
              {editingItem ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderLevelSelection = () => {
    return (
      <div className="mt-3 rounded-2xl border border-[hsl(var(--ds-border))] pt-3 pb-1 px-3">
        <h4 className="mb-3 text-sm font-semibold">Assignment</h4>
        <div className="space-y-2">
          <label className="flex cursor-pointer items-center space-x-2">
            <input
              type="radio"
              name="assignmentType"
              value="all"
              checked={formData.assignmentType === "all"}
              onChange={(e) => handleAssignmentTypeChange(e.target.value)}
              className="h-4 w-4"
            />
            <span className="text-sm">Assign All</span>
          </label>
          <label className="flex cursor-pointer items-center space-x-2">
            <input
              type="radio"
              name="assignmentType"
              value="filter"
              checked={formData.assignmentType === "filter"}
              onChange={(e) => handleAssignmentTypeChange(e.target.value)}
              className="h-4 w-4"
            />
            <span className="text-sm">Assign using filter</span>
          </label>
        </div>
        {formData.assignmentType === "filter" && formData.filterData && (
          <div className="mt-3 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-2">
            <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
              Filter applied: {formData.filterData.employeesData?.length || 0} employees selected
            </p>
            <button
              type="button"
              onClick={() => setShowFilters(true)}
              className="mt-2 text-xs text-[hsl(var(--ds-secondary))] underline hover:text-[hsl(var(--ds-primary))]"
            >
              Change Filter
            </button>
          </div>
        )}
        {formData.assignmentType === "filter" && !formData.filterData && (
          <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-2 dark:border-amber-800/50 dark:bg-amber-950/40">
            <p className="text-xs text-amber-900 dark:text-amber-200">Please apply a filter to continue</p>
            <button
              type="button"
              onClick={() => setShowFilters(true)}
              className="mt-2 text-xs font-semibold text-amber-900 underline dark:text-amber-200"
            >
              Open filter
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ds-page">
      <div className="ds-page-shell">
      <div className="ds-page-body flex min-h-0 flex-1 overflow-hidden">
        {/* Sub Sidebar â€” sticky below main nav, in-flow (no overlap of main content); shell matches Employee.jsx */}
        <aside
          className="ds-page-aside"
        >
          <div className="ds-page-aside-head">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))] dark:text-[hsl(var(--ds-muted-fg))]">
              {isCommunicationNavMode
                ? "Communication"
                : isRoleManagementNavMode
                ? "User Role Management"
                : "Global Settings"}
            </h4>
          </div>
          <div className="ds-page-aside-nav">
            {!isRoleManagementNavMode && (
              <>
                {canViewFlashNews && (
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("communication");
                    setActiveSubTab("flash_news");
                  }}
                  className={`block w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                    activeTab === "communication" && activeSubTab === "flash_news"
                      ? "ds-page-nav-item ds-page-nav-item-active"
                      : "ds-page-nav-item"
                  }`}
                >
                  Flash News
                </button>
                )}
                {canViewAnnouncements && (
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("communication");
                    setActiveSubTab("announcement");
                  }}
                  className={`block w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                    activeTab === "communication" && activeSubTab === "announcement"
                      ? "ds-page-nav-item ds-page-nav-item-active"
                      : "ds-page-nav-item"
                  }`}
                >
                  Announcements
                </button>
                )}
                {canViewPolls && (
                <>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("communication");
                    setActiveSubTab("polls_advanced");
                  }}
                  className={`block w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                    activeTab === "communication" && activeSubTab === "polls_advanced"
                      ? "ds-page-nav-item ds-page-nav-item-active"
                      : "ds-page-nav-item"
                  }`}
                >
                  Advanced polls
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("communication");
                    setActiveSubTab("polls_manage");
                  }}
                  className={`block w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                    activeTab === "communication" && activeSubTab === "polls_manage"
                      ? "ds-page-nav-item ds-page-nav-item-active"
                      : "ds-page-nav-item"
                  }`}
                >
                  Manage polls
                </button>
                </>
                )}
                {canViewPolicy && (
                <button
                  type="button"
                  onClick={() => setActiveTab("policy")}
                  className={`block w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                    activeTab === "policy"
                      ? "ds-page-nav-item ds-page-nav-item-active"
                      : "ds-page-nav-item"
                  }`}
                >
                  Company Policy
                </button>
                )}
              </>
            )}
            {!isCommunicationNavMode && canViewUserRoles && (
              <button
                type="button"
                onClick={() => {
                  setActiveTab("user_role_management");
                  setActiveRoleSubTab("role_creation");
                }}
                className={`block w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                  activeTab === "user_role_management"
                    ? "ds-page-nav-item ds-page-nav-item-active"
                    : "ds-page-nav-item"
                }`}
              >
                User Role Management
              </button>
            )}
            {/* Workflow Management tab hidden/commented for now */}
          </div>

          {!isRoleManagementNavMode && (
            <div className="shrink-0 border-t border-[hsl(var(--ds-border))] p-2 pt-3 dark:border-[hsl(var(--ds-border))]">
              <div className="rounded-2xl bg-[hsl(var(--ds-secondary))]/30 p-4 shadow-inner ring-1 ring-[hsl(var(--ds-border))] dark:ring-[hsl(var(--ds-border))]">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--ds-primary))] dark:text-[hsl(var(--ds-muted-fg))]">
                  Data tools
                </p>
                <p className="mt-1 text-xs leading-snug text-[hsl(var(--ds-muted-fg))] dark:text-[hsl(var(--ds-muted-fg))]">
                  Import, export, or download communication templates.
                </p>
                <div className="mt-3 space-y-1">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-[hsl(var(--ds-primary))] transition-colors hover:bg-[hsl(var(--ds-muted))] dark:text-[hsl(var(--ds-fg))] dark:hover:bg-[hsl(var(--ds-muted))]"
                    onClick={() =>
                      toast("Use Flash News, Announcements, and Policy screens to add content. Bulk import may be enabled by your administrator.", {
                        duration: 4000,
                      })
                    }
                  >
                    <FileUp className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                    Import
                  </button>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-[hsl(var(--ds-primary))] transition-colors hover:bg-[hsl(var(--ds-muted))] dark:text-[hsl(var(--ds-fg))] dark:hover:bg-[hsl(var(--ds-muted))]"
                    onClick={() =>
                      toast("Export is available from each list where supported (e.g. export poll data from Manage polls).", {
                        duration: 4000,
                      })
                    }
                  >
                    <Upload className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                    Export
                  </button>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-[hsl(var(--ds-primary))] transition-colors hover:bg-[hsl(var(--ds-muted))] dark:text-[hsl(var(--ds-fg))] dark:hover:bg-[hsl(var(--ds-muted))]"
                    onClick={() =>
                      toast("Download templates from the relevant communication or policy screen when available.", {
                        duration: 4000,
                      })
                    }
                  >
                    <Download className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                    Download
                  </button>
                </div>
              </div>
            </div>
          )}
        </aside>
        {/* Tab content — flex sibling; aside width reserved in layout */}
        <div className="ds-page-main">
          <div className="ds-page-content-flush flex min-h-0 flex-1 flex-col overflow-hidden">
          {activeTab === "communication" && (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Sub-tabs for Communication */}
              {!isCommunicationNavMode && (
              <div className="flex space-x-4 border-b border-[hsl(var(--ds-border))] px-4">
                <button
                  onClick={() => setActiveSubTab("flash_news")}
                  disabled={!canViewFlashNews}
                  className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                    activeSubTab === "flash_news"
                      ? "text-[hsl(var(--ds-primary))] border-[hsl(var(--ds-primary))]"
                      : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] border-transparent"
                  } ${!canViewFlashNews ? "cursor-not-allowed opacity-45" : ""}`}
                >
                  Flash News
                </button>
                <button
                  onClick={() => setActiveSubTab("announcement")}
                  disabled={!canViewAnnouncements}
                  className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                    activeSubTab === "announcement"
                      ? "text-[hsl(var(--ds-primary))] border-[hsl(var(--ds-primary))]"
                      : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] border-transparent"
                  } ${!canViewAnnouncements ? "cursor-not-allowed opacity-45" : ""}`}
                >
                  Announcements
                </button>
                <button
                  onClick={() => setActiveSubTab("polls_advanced")}
                  disabled={!canViewPolls}
                  className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                    activeSubTab === "polls_advanced"
                      ? "text-[hsl(var(--ds-primary))] border-[hsl(var(--ds-primary))]"
                      : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] border-transparent"
                  } ${!canViewPolls ? "cursor-not-allowed opacity-45" : ""}`}
                >
                  Advanced polls
                </button>
                <button
                  onClick={() => setActiveSubTab("polls_manage")}
                  disabled={!canViewPolls}
                  className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                    activeSubTab === "polls_manage"
                      ? "text-[hsl(var(--ds-primary))] border-[hsl(var(--ds-primary))]"
                      : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] border-transparent"
                  } ${!canViewPolls ? "cursor-not-allowed opacity-45" : ""}`}
                >
                  Manage polls
                </button>
              </div>
              )}
              {!canViewCommunication && !canViewPolls ? (
                <div className="flex flex-1 items-center justify-center p-8">
                  <div className="max-w-lg rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    You do not have access to Communication content for this role.
                  </div>
                </div>
              ) : null}

              {/* Flash News Content */}
              {activeSubTab === "flash_news" && canViewFlashNews && (
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex justify-between items-center p-4 border-b border-[hsl(var(--ds-border))] flex-shrink-0">
                    <h3 className="text-lg font-semibold">Flash News</h3>
                    <button
                      onClick={() => {
                        if (!canCreateFlashNews) {
                          toast.error("You do not have permission to add flash news.");
                          return;
                        }
                        resetForm();
                        setEditingItem(null);
                        setShowFlashNewsForm(true);
                      }}
                      disabled={!canCreateFlashNews}
                      className={`flex items-center space-x-2 inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 px-4 py-2 ${!canCreateFlashNews ? "cursor-not-allowed opacity-50" : ""}`}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Flash News</span>
                    </button>
                  </div>

                  <div className="flex-1 p-4 overflow-hidden flex flex-col">
                    {/* Search Bar */}
                    <div className="mb-4 flex-shrink-0">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(var(--ds-muted-fg))] w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Search flash news..."
                          value={searchFlashNews}
                          onChange={(e) => setSearchFlashNews(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                        />
                      </div>
                    </div>

                    {flashNews.length === 0 ? (
                      <p className="text-[hsl(var(--ds-muted-fg))] text-center py-4">No flash news added yet</p>
                    ) : flashNews.filter(item => 
                        !searchFlashNews || 
                        item.title?.toLowerCase().includes(searchFlashNews.toLowerCase()) ||
                        item.content?.toLowerCase().includes(searchFlashNews.toLowerCase())
                      ).length === 0 ? (
                      <p className="text-[hsl(var(--ds-muted-fg))] text-center py-4">No flash news found matching your search</p>
                    ) : (
                      <div className="h-full overflow-y-auto flex-1">
                        <div className="grid grid-cols-1 gap-2" style={{ minHeight: flashNews.filter(item => 
                          !searchFlashNews || 
                          item.title?.toLowerCase().includes(searchFlashNews.toLowerCase()) ||
                          item.content?.toLowerCase().includes(searchFlashNews.toLowerCase())
                        ).length <= 10 ? "auto" : "1000px" }}>
                          {flashNews.filter(item => 
                            !searchFlashNews || 
                            item.title?.toLowerCase().includes(searchFlashNews.toLowerCase()) ||
                            item.content?.toLowerCase().includes(searchFlashNews.toLowerCase())
                          ).map((item) => (
                          <div
                            key={item.id}
                            className="relative flex items-center justify-between rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-3 transition-colors hover:bg-[hsl(var(--ds-muted))]"
                          >
                            <div className="flex flex-1 items-center gap-3 pr-8">
                              <div className="min-w-0 flex-1">
                                <h4 className="truncate text-sm font-semibold">{item.title}</h4>
                                {(item.displayFrom || item.displayUntil) && (
                                  <p className="mt-0.5 truncate text-[10px] text-[hsl(var(--ds-muted-fg))]">
                                    {item.displayFrom
                                      ? new Date(item.displayFrom).toLocaleString()
                                      : "—"}{" "}
                                    →{" "}
                                    {item.displayUntil
                                      ? new Date(item.displayUntil).toLocaleString()
                                      : "—"}
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="rounded-full bg-[hsl(var(--ds-accent))] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--ds-secondary))]">
                                  {item.recurringType === "none" ? "One-time" : 
                                   item.recurringType === "daily" ? "Daily" :
                                   item.recurringType === "weekly" ? `Weekly (${item.weekday || "N/A"})` :
                                   item.recurringType === "monthly" ? `Monthly (${item.selectedDates?.map(d => `Day ${d.day}`).join(", ") || "N/A"})` :
                                   item.recurringType === "custom_months" ? `Custom Months (${item.selectedDates?.map(d => `${d.month} ${d.day}`).join(", ") || "N/A"})` :
                                   item.recurringType === "custom" ? `Custom (${item.startDate ? new Date(item.startDate).toLocaleDateString() : ""} - ${item.endDate ? new Date(item.endDate).toLocaleDateString() : ""})` :
                                   item.recurringType || "None"}
                                </span>
                                {item.assignmentType === "all" && (
                                  <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300">All Employees</span>
                                )}
                                {item.assignmentType === "filter" && item.filterData && (
                                  <span className="rounded border border-[hsl(var(--ds-secondary)/0.25)] bg-[hsl(var(--ds-accent))] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--ds-secondary))] dark:border-[hsl(var(--ds-secondary)/0.5)] dark:bg-[hsl(var(--ds-secondary)/0.3)] dark:text-[hsl(var(--ds-accent-fg))]">
                                    Filter ({item.filterData.employeesData?.length || 0})
                                  </span>
                                )}
                                {item.status === "inactive" && (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-800 text-[10px] font-medium">Inactive</span>
                                )}
                              </div>
                            </div>
                            <div className="relative" ref={openMenuId === item.id ? menuRef : null}>
                              {(canEditFlashNews || canDeleteFlashNews) && (
                                <button
                                  onClick={() => setOpenMenuId(openMenuId === item.id ? null : item.id)}
                                  className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] p-1"
                                >
                                  <MoreVertical className="w-5 h-5" />
                                </button>
                              )}
                              {openMenuId === item.id && (
                                <div className="absolute right-0 top-full mt-1 w-32 bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-lg z-50">
                                  <button
                                    onClick={() => {
                                      setPreviewItem(item);
                                      setShowPreview(true);
                                      setOpenMenuId(null);
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--ds-muted))] flex items-center space-x-2"
                                  >
                                    <Eye className="w-4 h-4" />
                                    <span>Preview</span>
                                  </button>
                                  <button
                                    onClick={() => {
                                      handleEdit(item, "flash_news");
                                      setOpenMenuId(null);
                                    }}
                                    disabled={!canEditFlashNews}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--ds-muted))] flex items-center space-x-2"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                    <span>Edit</span>
                                  </button>
                                  {item.status !== "inactive" && canEditFlashNews && (
                                    <button
                                      onClick={() => handleDeactivate("flash_news", item.id)}
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--ds-muted))] flex items-center space-x-2 text-orange-600"
                                    >
                                      <Settings className="w-4 h-4" />
                                      <span>Deactivate</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDelete("flash_news", item.id)}
                                    disabled={!canDeleteFlashNews}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--ds-muted))] flex items-center space-x-2 text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    <span>Delete</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Announcements Content */}
              {activeSubTab === "announcement" && canViewAnnouncements && (
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex justify-between items-center p-4 border-b border-[hsl(var(--ds-border))] flex-shrink-0">
                    <h3 className="text-lg font-semibold">Announcements</h3>
                    <button
                      onClick={() => {
                        if (!canCreateAnnouncements) {
                          toast.error("You do not have permission to add announcements.");
                          return;
                        }
                        resetForm();
                        setFormData({ ...formData, type: "announcement" });
                        setShowAnnouncementForm(true);
                      }}
                      disabled={!canCreateAnnouncements}
                      className={`flex items-center space-x-2 inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 px-4 py-2 ${!canCreateAnnouncements ? "cursor-not-allowed opacity-50" : ""}`}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Announcement</span>
                    </button>
                  </div>

                  <div className="flex-1 p-4 overflow-hidden flex flex-col">
                    {/* Search Bar */}
                    <div className="mb-4 flex-shrink-0">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(var(--ds-muted-fg))] w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Search announcements..."
                          value={searchAnnouncements}
                          onChange={(e) => setSearchAnnouncements(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-[hsl(var(--ds-border))] rounded text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                        />
                      </div>
                    </div>

                    {announcements.length === 0 ? (
                      <p className="text-[hsl(var(--ds-muted-fg))] text-center py-4">No announcements added yet</p>
                    ) : announcements.filter(item => 
                        !searchAnnouncements || 
                        item.title?.toLowerCase().includes(searchAnnouncements.toLowerCase()) ||
                        item.content?.toLowerCase().includes(searchAnnouncements.toLowerCase())
                      ).length === 0 ? (
                      <p className="text-[hsl(var(--ds-muted-fg))] text-center py-4">No announcements found matching your search</p>
                    ) : (
                      <div className="h-full overflow-y-auto flex-1">
                        <div className="grid grid-cols-1 gap-2" style={{ minHeight: announcements.filter(item => 
                          !searchAnnouncements || 
                          item.title?.toLowerCase().includes(searchAnnouncements.toLowerCase()) ||
                          item.content?.toLowerCase().includes(searchAnnouncements.toLowerCase())
                        ).length <= 10 ? "auto" : "1000px" }}>
                          {announcements.filter(item => 
                            !searchAnnouncements || 
                            item.title?.toLowerCase().includes(searchAnnouncements.toLowerCase()) ||
                            item.content?.toLowerCase().includes(searchAnnouncements.toLowerCase())
                          ).map((item) => (
                          <div
                            key={item.id}
                            className="bg-[hsl(var(--ds-muted))] p-3 border border-[hsl(var(--ds-border))] flex justify-between items-center relative hover:bg-[hsl(var(--ds-muted))] transition-colors"
                          >
                            <div className="flex-1 pr-8 flex items-center gap-3">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-sm truncate">{item.title}</h4>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-2 py-0.5 bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))] text-[10px] font-medium">
                                  {item.recurringType === "none" ? "One-time" : 
                                   item.recurringType === "daily" ? "Daily" :
                                   item.recurringType === "weekly" ? `Weekly (${item.weekday || "N/A"})` :
                                   item.recurringType === "monthly" ? `Monthly (${item.selectedDates?.map(d => `Day ${d.day}`).join(", ") || "N/A"})` :
                                   item.recurringType === "custom_months" ? `Custom Months (${item.selectedDates?.map(d => `${d.month} ${d.day}`).join(", ") || "N/A"})` :
                                   item.recurringType === "custom" ? `Custom (${item.startDate ? new Date(item.startDate).toLocaleDateString() : ""} - ${item.endDate ? new Date(item.endDate).toLocaleDateString() : ""})` :
                                   item.recurringType || "None"}
                                </span>
                                {item.assignmentType === "all" && (
                                  <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300">All Employees</span>
                                )}
                                {item.assignmentType === "filter" && item.filterData && (
                                  <span className="rounded border border-[hsl(var(--ds-secondary)/0.25)] bg-[hsl(var(--ds-accent))] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--ds-secondary))] dark:border-[hsl(var(--ds-secondary)/0.5)] dark:bg-[hsl(var(--ds-secondary)/0.3)] dark:text-[hsl(var(--ds-accent-fg))]">
                                    Filter ({item.filterData.employeesData?.length || 0})
                                  </span>
                                )}
                                {item.status === "inactive" && (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-800 text-[10px] font-medium">Inactive</span>
                                )}
                              </div>
                            </div>
                            <div className="relative" ref={openMenuId === item.id ? menuRef : null}>
                              {(canEditAnnouncements || canDeleteAnnouncements) && (
                                <button
                                  onClick={() => setOpenMenuId(openMenuId === item.id ? null : item.id)}
                                  className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] p-1"
                                >
                                  <MoreVertical className="w-5 h-5" />
                                </button>
                              )}
                              {openMenuId === item.id && (
                                <div className="absolute right-0 top-full mt-1 w-32 bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-lg z-50">
                                  <button
                                    onClick={() => {
                                      handleEdit(item, "announcement");
                                      setOpenMenuId(null);
                                    }}
                                    disabled={!canEditAnnouncements}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--ds-muted))] flex items-center space-x-2"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                    <span>Edit</span>
                                  </button>
                                  {item.status !== "inactive" && canEditAnnouncements && (
                                    <button
                                      onClick={() => handleDeactivate("announcement", item.id)}
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--ds-muted))] flex items-center space-x-2 text-orange-600"
                                    >
                                      <Settings className="w-4 h-4" />
                                      <span>Deactivate</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDelete("announcement", item.id)}
                                    disabled={!canDeleteAnnouncements}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--ds-muted))] flex items-center space-x-2 text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    <span>Delete</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeSubTab === "polls_advanced" && canViewPolls && (
                <CommunicationPollsPanel
                  mode="advanced"
                  canCreatePolls={canCreatePolls}
                  canEditPolls={canEditPolls}
                  canDeletePolls={canDeletePolls}
                />
              )}
              {activeSubTab === "polls_manage" && canViewPolls && (
                <CommunicationPollsPanel
                  mode="manage"
                  onNavigateAdvanced={() => setActiveSubTab("polls_advanced")}
                  canCreatePolls={canCreatePolls}
                  canEditPolls={canEditPolls}
                  canDeletePolls={canDeletePolls}
                />
              )}
            </div>
          )}

          {activeTab === "policy" && canViewPolicy && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex justify-between items-center p-4 border-b border-[hsl(var(--ds-border))] flex-shrink-0">
                <h3 className="text-lg font-semibold">Company Policies</h3>
                <button
                  onClick={() => {
                    resetForm();
                    setShowPolicyForm(true);
                  }}
                  disabled={!canCreatePolicy}
                  className="flex items-center space-x-2 inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 px-4 py-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Policy</span>
                </button>
              </div>

              <div className="flex-1 p-4 overflow-hidden">
                {policies.length === 0 ? (
                  <p className="text-[hsl(var(--ds-muted-fg))] text-center py-4">No policies added yet</p>
                ) : (
                  <div className="h-full overflow-y-auto">
                    <div className="grid grid-cols-1 gap-2">
                      {policies.map((item) => (
                        <div
                          key={item.id}
                          className="bg-[hsl(var(--ds-muted))] p-4 border border-[hsl(var(--ds-border))] flex justify-between items-start"
                        >
                          <div className="flex-1">
                            <h4 className="font-semibold">{item.policyTitle || item.name}</h4>
                            <div className="text-sm text-[hsl(var(--ds-muted-fg))] mt-1">
                              <p>Code: {item.policyCode || "N/A"}</p>
                              <p>Category: {item.policyCategory || "N/A"}</p>
                              <p>Type: {item.policyType === "mandatory" ? "Mandatory" : "Optional"}</p>
                              <p>Version: {item.version || "1.0"}</p>
                              <p>Effective From: {item.effectiveFrom ? new Date(item.effectiveFrom).toLocaleDateString() : "N/A"}</p>
                              {item.documentUrl && (
                                <a
                                  href={item.documentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[hsl(var(--ds-secondary))] hover:underline mt-1 inline-block"
                                >
                                  View Document
                                </a>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(item, "policy")}
                              disabled={!canEditPolicy}
                              className="text-[hsl(var(--ds-secondary))] hover:text-[hsl(var(--ds-primary))] disabled:opacity-45"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete("policy", item.id)}
                              disabled={!canDeletePolicy}
                              className="text-red-600 hover:text-red-800 disabled:opacity-45"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "user_role_management" && canViewUserRoles && (
            <UserRoleManagement
              currentUserRole={currentUserRole}
              roles={roles}
              fetchRoles={fetchRoles}
              rolesMeta={rolesMeta}
              setRoles={setRoles}
              formData={formData}
              setFormData={setFormData}
              handleInputChange={handleInputChange}
              filterResults={filterResults}
              setFilterResults={setFilterResults}
              setShowFilters={setShowFilters}
              branches={branches}
              departments={departments}
            />
          )}

          {/* Workflow Management Tab */}
          {activeTab === "workflow_management" &&
            (currentUserRole === "super_admin" || currentUserRole === "admin") && (
            <WorkflowManagement 
              currentUserRole={currentUserRole}
              roles={roles}
              onClose={() => {}}
            />
          )}

          </div>
      </div>
      </div>

      {/* Form Popups */}
      {renderFormPopup("flash_news")}
      {renderFormPopup("announcement")}
      {renderPolicyFormPopup()}

      {/* Filters Modal */}
      {showFilters && (
        <div className="fixed inset-0 z-[10020]">
          <Filters
            onApply={handleFilterApply}
            onClose={handleFilterClose}
            darkMode={false}
            initialFilters={formData.filterData?.filters || {}}
          />
        </div>
      )}

      {/* Calendar Popup */}
      {showCalendarPopup && calendarForMonth && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[90]">
          <div className="bg-[hsl(var(--ds-surface))] shadow-xl relative w-[500px] max-h-[80vh] flex flex-col border border-[hsl(var(--ds-border))]">
            <div className="flex items-center justify-between p-4 border-b border-[hsl(var(--ds-border))] flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold">Select Dates for {calendarForMonth}</h3>
                {formData.months && formData.months.length > 1 && (
                  <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">
                    Month {formData.months.indexOf(calendarForMonth) + 1} of {formData.months.length}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowCalendarPopup(false);
                  setCalendarForMonth(null);
                }}
                className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Select Days (1-31)</label>
                <div className="grid grid-cols-10 gap-1">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => {
                    const isSelected = formData.selectedDates?.some(d => d.month === calendarForMonth && d.day === day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => handleCalendarDateToggle(day)}
                        className={`w-8 h-8 text-xs border ${
                          isSelected
                            ? "inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 border-[hsl(var(--ds-primary))]"
                            : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
              {formData.selectedDates && formData.selectedDates.filter(d => d.month === calendarForMonth).length > 0 && (
                <div className="mt-4 p-3 bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))]">
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <p className="text-sm font-medium mb-1">Selected Dates:</p>
                      <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                        {formData.selectedDates
                          .filter(d => d.month === calendarForMonth)
                          .map(d => `${calendarForMonth} ${d.day}`)
                          .join(", ")}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleClearCalendarDates}
                      className="text-red-600 hover:text-red-800 text-xs font-medium px-2 py-1"
                    >
                      Clear Dates
                    </button>
                  </div>
                </div>
              )}
              {formData.months && formData.months.length > 1 && (
                <div className="mt-4 p-3 bg-[hsl(var(--ds-accent))] border border-[hsl(var(--ds-border))]">
                  <p className="text-xs text-[hsl(var(--ds-secondary))]">
                    <strong>Note:</strong> You have selected multiple months. After closing this calendar, you can click the Calendar button again to select dates for other months.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-between p-4 border-t border-[hsl(var(--ds-border))] flex-shrink-0">
              {formData.months && formData.months.length > 1 && (
                <button
                  onClick={() => {
                    const currentIndex = formData.months.indexOf(calendarForMonth);
                    if (currentIndex > 0) {
                      setCalendarForMonth(formData.months[currentIndex - 1]);
                    }
                  }}
                  disabled={formData.months.indexOf(calendarForMonth) === 0}
                  className="bg-[hsl(var(--ds-muted))] text-white px-4 py-2 hover:bg-[hsl(var(--ds-muted))] disabled:bg-[hsl(var(--ds-muted))] disabled:cursor-not-allowed"
                >
                  Previous Month
                </button>
              )}
              <div className="flex gap-2 ml-auto">
                <button
                  onClick={() => {
                    setShowCalendarPopup(false);
                    setCalendarForMonth(null);
                  }}
                  className="bg-[hsl(var(--ds-muted))] text-white px-4 py-2 hover:bg-[hsl(var(--ds-muted))]"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // If there are multiple months selected, open calendar for next month
                    if (formData.months && formData.months.length > 1) {
                      const currentIndex = formData.months.indexOf(calendarForMonth);
                      if (currentIndex < formData.months.length - 1) {
                        setCalendarForMonth(formData.months[currentIndex + 1]);
                      } else {
                        setShowCalendarPopup(false);
                        setCalendarForMonth(null);
                      }
                    } else {
                      setShowCalendarPopup(false);
                      setCalendarForMonth(null);
                    }
                  }}
                  className="inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 px-4 py-2"
                >
                  {formData.months && formData.months.length > 1 && formData.months.indexOf(calendarForMonth) < formData.months.length - 1
                    ? "Next Month"
                    : "Done"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal - Flowing Banner */}
      {showPreview && previewItem && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[hsl(var(--ds-fg))/0.45] p-4">
          <div className="relative flex h-[220px] w-[90vw] max-w-[1100px] flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
            <div className="flex flex-shrink-0 items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
              <div>
                <h3 className="ds-h3">Flash News Preview</h3>
                <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                  Navbar-style rolling highlight (right → left)
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowPreview(false);
                  setPreviewItem(null);
                }}
                className="rounded-full p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
              >
                <X size={20} />
              </button>
            </div>
            <div className="relative flex-1 overflow-hidden rounded-b-2xl bg-[hsl(var(--ds-warning)/0.14)]">
              <div className="absolute inset-0 flex items-center overflow-hidden px-4">
                <div className="pace-flash-marquee flex whitespace-nowrap" style={{ animationDuration: "18s" }}>
                  <div className="flex items-center gap-10 pr-10">
                    <span className="text-sm font-semibold text-[hsl(var(--ds-warning))] sm:text-lg">
                      ✦ {previewItem.title} — {previewItem.content}
                    </span>
                    <span className="text-sm font-semibold text-[hsl(var(--ds-warning))] sm:text-lg" aria-hidden>
                      ✦ {previewItem.title} — {previewItem.content}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Acknowledgement Settings Modal */}
      {showAcknowledgementSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[80]">
          <div className="bg-[hsl(var(--ds-surface))] shadow-xl relative w-[500px] max-h-[80vh] flex flex-col border border-[hsl(var(--ds-border))]">
            {/* Fixed Header */}
            <div className="flex items-center justify-between p-4 border-b border-[hsl(var(--ds-border))] flex-shrink-0">
              <h3 className="text-xl font-bold">Acknowledgement Settings</h3>
              <button
                onClick={() => {
                  setShowAcknowledgementSettings(false);
                }}
                className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
              >
                <X size={24} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Require acknowledgement</label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="requireAcknowledgement"
                        value="yes"
                        checked={requireAcknowledgement === true}
                        onChange={() => setRequireAcknowledgement(true)}
                        className="mr-2"
                      />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="requireAcknowledgement"
                        value="no"
                        checked={requireAcknowledgement === false}
                        onChange={() => setRequireAcknowledgement(false)}
                        className="mr-2"
                      />
                      <span className="text-sm">No</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Action Buttons */}
            <div className="flex justify-end space-x-2 p-4 border-t border-[hsl(var(--ds-border))] flex-shrink-0">
              <button
                onClick={() => {
                  setShowAcknowledgementSettings(false);
                }}
                className="bg-[hsl(var(--ds-muted))] text-white px-4 py-2 hover:bg-[hsl(var(--ds-muted))]"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Save the acknowledgement setting here
                  // You can add API call or state management logic here
                  console.log("Require Acknowledgement:", requireAcknowledgement);
                  setShowAcknowledgementSettings(false);
                }}
                className="inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 rounded-md px-4 py-2 hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
